import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import '../styles/SurveyResponseDetail.css';

const SurveyResponseDetail = () => {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const { 
    currentUser, 
    surveyAssignments, 
    surveyTemplates, 
    patients,
    getPatientById
  } = useAuth();
  
  const [surveyResponse, setSurveyResponse] = useState(null);
  const [surveyTemplate, setSurveyTemplate] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isSigningOff, setIsSigningOff] = useState(false);

  // Handle sign off action
  const handleSignOff = async () => {
    if (!surveyResponse || !currentUser) return;
    
    try {
      setIsSigningOff(true);
      console.log('Marking response as reviewed:', surveyResponse.id);
      
      // Update the assignment in Firestore
      const assignmentRef = doc(db, 'surveyAssignments', surveyResponse.id);
      await updateDoc(assignmentRef, {
        isReviewed: true,
        reviewedBy: currentUser.uid,
        reviewedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setSurveyResponse(prev => ({
        ...prev,
        isReviewed: true,
        reviewedBy: currentUser.uid,
        reviewedAt: new Date().toISOString()
      }));
      
      console.log('Successfully marked as reviewed');
      
    } catch (error) {
      console.error('Error marking response as reviewed:', error);
      setError('Failed to mark response as reviewed. Please try again.');
    } finally {
      setIsSigningOff(false);
    }
  };

  // Fetch survey response data when component mounts or responseId changes
  useEffect(() => {
    const fetchSurveyResponse = async () => {
      if (!responseId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('🔍 Fetching survey response data for:', responseId);
        
        // 1. Get the survey assignment/response
        const responseDoc = await getDoc(doc(db, 'surveyAssignments', responseId));
        if (!responseDoc.exists()) {
          throw new Error('Survey response not found');
        }
        
        const responseData = {
          id: responseDoc.id,
          ...responseDoc.data()
        };
        
        console.log('📄 Survey response data:', responseData);
        setSurveyResponse(responseData);
        
        // 2. Get the survey template
        const templateId = responseData.templateId || responseData.surveyId;
        if (!templateId) {
          throw new Error('No template ID found in survey response');
        }
        
        const templateDoc = await getDoc(doc(db, 'surveyTemplates', templateId));
        if (!templateDoc.exists()) {
          throw new Error('Survey template not found');
        }
        
        const templateData = {
          id: templateDoc.id,
          ...templateDoc.data()
        };
        
        console.log('📋 Survey template data:', templateData);
        setSurveyTemplate(templateData);
        
        // 3. Get the patient data if available
        if (responseData.patientId) {
          const patientData = await getPatientById(responseData.patientId);
          if (patientData) {
            setPatient(patientData);
          }
        }
        
        setIsDataReady(true);
      } catch (err) {
        console.error('Error loading survey response:', err);
        setError(err.message || 'Failed to load survey response');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSurveyResponse();
  }, [responseId, getPatientById]);

  // Helper function to get question text
  const getQuestionText = (question) => {
    console.log('Getting question text for:', question);
    
    if (!question) {
      console.log('No question provided');
      return 'Question';
    }
    
    // First, check if we have a direct question text (highest priority)
    if (question.question !== undefined && question.question !== null) {
      const text = String(question.question).trim();
      if (text !== '') {
        console.log('Using question.question:', text);
        return text;
      }
    }
    
    // For debugging - log what fields are available
    console.log('Question fields:', Object.keys(question).filter(k => typeof question[k] === 'string'));
    
    // Check other possible text fields in priority order
    const possibleTextFields = [
      'text',
      'questionText',
      'label',
      'title',
      'name',
      'prompt',
      'description',
      'header',
      'heading',
      'content',
      'id'  // Last resort - use ID if nothing else is available
    ];
    
    // Find the first non-empty text field
    for (const field of possibleTextFields) {
      if (question[field] !== undefined && question[field] !== null) {
        const text = String(question[field]).trim();
        if (text !== '') {
          console.log(`Using ${field}:`, text);
          return text;
        }
      }
    }
    
    // If we have a content object, try to get text from it
    if (question.content && typeof question.content === 'object') {
      for (const field of possibleTextFields) {
        if (question.content[field] !== undefined && question.content[field] !== null) {
          const text = String(question.content[field]).trim();
          if (text !== '') {
            console.log(`Using content.${field}:`, text);
            return text;
          }
        }
      }
    }
    
    // Fallback to ID if nothing else works
    const fallback = `Question ${question.id || '#'}`;
    console.log('Using fallback text:', fallback);
    return fallback;
  };

  // Helper to render response value based on question type
  const renderResponseValue = (value, question) => {
    console.log('Rendering response value:', { value, question });
    
    // Handle null/undefined/empty values
    if (value === null || value === undefined || value === '') {
      return <p className="no-response">No response provided</p>;
    }

    // Handle different question types
    if (question?.type === 'multiple-choice' || question?.type === 'select') {
      const options = Array.isArray(question.options) ? question.options : [];
      
      // Handle array responses (multiple selections)
      if (Array.isArray(value)) {
        if (value.length === 0) return <p className="no-response">No selection</p>;
        
        return (
          <ul className="response-list">
            {value.map((val, i) => {
              // Try to find the option by value or use the raw value
              const option = options.find(opt => 
                (opt.value !== undefined && opt.value === val) || 
                opt === val ||
                opt.label === val
              );
              return <li key={i}>{option?.label || option?.value || val}</li>;
            })}
          </ul>
        );
      }
      
      // Handle single selection
      const option = options.find(opt => 
        (opt.value !== undefined && opt.value === value) || 
        opt === value ||
        opt.label === value
      );
      return <p>{option?.label || option?.value || value}</p>;
    }
    
    // Handle checkbox responses (can be array or single value)
    if (question?.type === 'checkbox') {
      if (Array.isArray(value)) {
        return (
          <ul className="response-list">
            {value.map((val, i) => (
              <li key={i}>
                <span className="checkbox-response">✓</span> {val}
              </li>
            ))}
          </ul>
        );
      }
      return <p><span className="checkbox-response">{value ? '✓' : '✗'}</span> {value ? 'Yes' : 'No'}</p>;
    }
    
    // Handle rating responses
    if (question?.type === 'rating') {
      const rating = typeof value === 'number' ? value : parseInt(value, 10);
      if (isNaN(rating)) return <p>{value}</p>; // Fallback for non-numeric ratings
      
      return (
        <div className="rating-response">
          {[...Array(5)].map((_, i) => (
            <span key={i} className={i < rating ? 'star-filled' : 'star-empty'}>★</span>
          ))}
          <span className="rating-value">({rating})</span>
        </div>
      );
    }
    
    // Handle yes/no questions
    if (question?.type === 'yes-no') {
      const boolValue = typeof value === 'string' 
        ? value.toLowerCase() === 'yes' || value.toLowerCase() === 'true'
        : Boolean(value);
      return <p>{boolValue ? 'Yes' : 'No'}</p>;
    }
    
    // Handle array responses (for other question types)
    if (Array.isArray(value)) {
      if (value.length === 0) return <p className="no-response">No response</p>;
      
      return (
        <ul className="response-list">
          {value.map((item, i) => (
            <li key={i}>
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </li>
          ))}
        </ul>
      );
    }
    
    // Handle object responses (like for complex question types)
    if (typeof value === 'object') {
      return (
        <div className="object-response">
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="response-field">
              <strong>{key}:</strong> {String(val)}
            </div>
          ))}
        </div>
      );
    }
    
    // Default case - convert to string and display
    return <p>{String(value)}</p>;
  };

  // Get matched and unmatched responses
  const getMatchedResponses = useCallback(() => {
    if (!surveyTemplate || !surveyResponse || !surveyTemplate.questions) {
      console.log('Missing data for matched responses:', {
        hasTemplate: !!surveyTemplate,
        hasResponse: !!surveyResponse,
        hasQuestions: surveyTemplate?.questions?.length > 0
      });
      return [];
    }
    
    // Check both responses and answers fields
    const responseData = surveyResponse.responses || surveyResponse.answers || {};
    
    console.log('Getting matched responses:', {
      templateId: surveyTemplate.id,
      responseId: surveyResponse.id,
      status: surveyResponse.status,
      questionCount: surveyTemplate.questions.length,
      responseKeys: Object.keys(responseData),
      hasResponses: Object.keys(surveyResponse.responses || {}).length > 0,
      hasAnswers: Object.keys(surveyResponse.answers || {}).length > 0,
      responseData: JSON.stringify(responseData, null, 2) // For debugging
    });
    
    // Create a map of all response keys to their values for faster lookup
    const responseKeyMap = new Map();
    Object.entries(responseData).forEach(([key, value]) => {
      // Normalize the key for case-insensitive matching
      const normalizedKey = key.toString().toLowerCase().trim();
      responseKeyMap.set(normalizedKey, { originalKey: key, value });
    });
    
    // Process all questions to find matching responses
    return surveyTemplate.questions
      .map(question => {
        if (!question || !question.id) return null;
        
        // Get the question text from the most likely fields
        const questionText = question.question || question.text || question.label || '';
        
        // Try to find a matching response using different key formats
        const possibleKeys = [
          // Exact matches first
          question.id,
          `q${question.id}`,
          `question_${question.id}`,
          
          // Text-based matches (only if we have question text)
          ...(questionText ? [
            questionText.toLowerCase().trim(),
            questionText.toLowerCase().trim().replace(/[^a-z0-9]/g, '')
          ] : []),
          
          // Common variations
          `question_${question.id}_text`,
          `q${question.id}_text`,
          `question_${question.id}_value`,
          `q${question.id}_value`,
          
          // Fallback formats
          String(question.id),
          `q${String(question.id).replace('question_', '')}`,
          `question_${String(question.id).replace('q', '')}`,
          
          // Index-based fallbacks
          ...(question.index !== undefined ? [
            String(question.index),
            `q${question.index}`,
            `question_${question.index}`
          ] : [])
        ].filter(Boolean); // Remove any null/undefined keys
        
        // Try to find a matching response key (case-insensitive)
        let responseKey = null;
        let responseValue = null;
        
        // First try exact matches in the response data
        for (const key of possibleKeys) {
          if (key in responseData) {
            responseKey = key;
            responseValue = responseData[key];
            break;
          }
        }
        
        // If no exact match found, try case-insensitive matching
        if (!responseKey) {
          for (const key of possibleKeys) {
            const normalizedKey = key.toString().toLowerCase().trim();
            if (responseKeyMap.has(normalizedKey)) {
              const match = responseKeyMap.get(normalizedKey);
              responseKey = match.originalKey; // Use the original key for consistency
              responseValue = match.value;
              responseKeyMap.delete(normalizedKey); // Prevent duplicate matches
              break;
            }
          }
        }
        
        if (!responseKey) {
          console.log('No match found for question:', {
            questionId: question.id,
            questionText: question.text,
            availableKeys: Object.keys(responseData),
            possibleKeys: possibleKeys
          });
          return null;
        }
        
        console.log('Found response for question:', {
          questionId: question.id,
          responseKey,
          value: responseData[responseKey]
        });
        
        // Format the response based on question type
        let formattedResponse = responseData[responseKey];
        
        // Handle different question types
        if (question.type === 'checkbox' && Array.isArray(formattedResponse)) {
          formattedResponse = formattedResponse.join(', ');
        } else if (typeof formattedResponse === 'object' && formattedResponse !== null) {
          // Handle object responses (e.g., from complex question types)
          formattedResponse = JSON.stringify(formattedResponse);
        } else if (formattedResponse === null || formattedResponse === undefined) {
          formattedResponse = 'No response';
        }
        
        return {
          question: {
            ...question,
            text: question.text || `Question ${question.id}`
          },
          response: formattedResponse,
          responseKey
        };
      })
      .filter(Boolean); // Remove any null entries
  }, [surveyTemplate, surveyResponse]);

  // Get any unmatched responses with question text mapping
  const getUnmatchedResponses = () => {
    if (!surveyResponse?.responses) return [];

    const matchedKeys = new Set();
    const unmatched = [];
    const questionTextMap = {};
    const questionIdMap = {};

    // Build maps for question lookup
    if (surveyTemplate?.questions) {
      surveyTemplate.questions.forEach((question, index) => {
        const id = question.id?.toString();
        const textKey = (question.text || question.question || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        
        if (id) {
          // Map by ID
          questionTextMap[id] = question.text || question.question || `Question ${id}`;
          
          // Map by normalized text
          if (textKey) {
            questionIdMap[textKey] = id;
          }
          
          // Map by index (for custom surveys)
          questionIdMap[`q${index + 1}`] = id;
          questionIdMap[`question${index + 1}`] = id;
        }
      });
    }

    // First, collect all matched keys
    getMatchedResponses().forEach(({ responseKey }) => {
      if (responseKey) matchedKeys.add(responseKey);
    });

    // Then find all responses that weren't matched
    Object.entries(surveyResponse.responses).forEach(([key, value]) => {
      if (!matchedKeys.has(key) && key !== 'timestamp' && key !== 'date' && key !== 'completed') {
        // Try to find question text
        let questionText = questionTextMap[key];
        let isObjectResponse = false;
        
        // Try to find by normalized key
        const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!questionText && questionIdMap[normalizedKey]) {
          questionText = questionTextMap[questionIdMap[normalizedKey]] || key;
        }
        
        // Handle object responses (like for checkboxes)
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          isObjectResponse = true;
          if (!questionText) {
            questionText = key;
          }
        }
        
        // If we still don't have a question text, use the key
        if (!questionText) {
          // Try to extract question number from key (e.g., 'q1' -> 'Question 1')
          const match = key.match(/^q(\d+)$/i);
          if (match) {
            questionText = `Question ${match[1]}`;
          } else {
            questionText = key;
          }
        }

        unmatched.push({ 
          key, 
          value,
          questionText,
          isObjectResponse
        });
      }
    });

    return unmatched;
  };

  // Load data
  useEffect(() => {
    console.log('🔍 SurveyResponseDetail mounted or updated', { responseId });
    let isMounted = true;
    setLoading(true);
    setError(null);
    
    const loadData = async () => {
      console.log('📥 Starting data load for response:', responseId);
      
      try {
        console.log('🔍 Loading survey response data...', { responseId });
        
        // First, try to find the response in the context
        let response = surveyAssignments.find(a => a && a.id === responseId);
        let template = null;
        
        // If not found in context, try to fetch it directly
        if (!response && currentUser) {
          console.log('Response not found in context, trying to fetch directly...');
          try {
            const docRef = doc(db, 'surveyAssignments', responseId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              response = { 
                id: docSnap.id, 
                ...docSnap.data(),
                responses: docSnap.data().responses || {}
              };
              console.log('Fetched response from Firestore:', response);
            }
          } catch (error) {
            console.error('Error fetching response from Firestore:', error);
            if (isMounted) {
              setError('Failed to load survey response. Please try again.');
              setLoading(false);
            }
            return;
          }
        }
        
        // If not found in context, try to fetch it directly
        if (!response && currentUser) {
          console.log('Response not found in context, trying to fetch directly...');
          try {
            const docRef = doc(db, 'surveyAssignments', responseId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              response = { 
                id: docSnap.id, 
                ...docSnap.data(),
                responses: docSnap.data().responses || {}
              };
              console.log('Fetched response from Firestore:', response);
            }
          } catch (error) {
            console.error('Error fetching response from Firestore:', error);
          }
        }
        
        console.log('Survey assignments in context:', surveyAssignments);
        console.log('Survey templates in context:', surveyTemplates);
        
        // If we have a response but no template, try to find it in the context
        if (response?.surveyId && !template) {
          template = surveyTemplates.find(t => t.id === response.surveyId);
          if (template) {
            console.log('Found template in context for survey:', response.surveyId);
          }
        }
        
        // If still no template, try to fetch it from Firestore
        if (response?.surveyId && !template) {
          console.log('Template not found in context, fetching from Firestore...');
          try {
            const templateRef = doc(db, 'surveyTemplates', response.surveyId);
            const templateSnap = await getDoc(templateRef);
            if (templateSnap.exists()) {
              template = { id: templateSnap.id, ...templateSnap.data() };
              console.log('Fetched template from Firestore:', template);
            }
          } catch (error) {
            console.error('Error fetching template from Firestore:', error);
          }
        }
        
        // If we have a response but no template, log an error
        if (response && !template) {
          console.error('Could not find template for survey:', response.surveyId);
        }
        
        // If we have a response, ensure it has the necessary data
        if (response) {
          // Ensure responses is an object
          if (!response.responses || typeof response.responses !== 'object') {
            response = {
              ...response,
              responses: {}
            };
          }
          
          // If we have answers but no responses, use answers
          if (response.answers && Object.keys(response.answers).length > 0 && 
              (!response.responses || Object.keys(response.responses).length === 0)) {
            console.log('Using answers as responses');
            response.responses = { ...response.answers };
          }
          
          console.log('Processed response data:', {
            id: response.id,
            status: response.status,
            hasResponses: Object.keys(response.responses || {}).length > 0,
            hasAnswers: response.answers ? Object.keys(response.answers).length > 0 : false,
            responses: response.responses
          });
          
          // Set the response state
          if (isMounted) {
            setSurveyResponse(response);
            
            // Set patient info if available
            if (response.patientName || response.patientId) {
              const patientInfo = patients.find(p => p.id === response.patientId) || {};
              setPatient({
                id: response.patientId,
                name: response.patientName || patientInfo.name || 'Unknown Patient',
                ...patientInfo
              });
            }
          }
          
          // Find the template if we haven't already
          if (!template && response.surveyId) {
            template = surveyTemplates.find(t => t.id === response.surveyId);
            
            // If template not in context, try to fetch it
            if (!template) {
              console.log('Template not found in context, fetching from Firestore...');
              try {
                const templateRef = doc(db, 'surveyTemplates', response.surveyId);
                const templateSnap = await getDoc(templateRef);
                if (templateSnap.exists()) {
                  template = { 
                    id: templateSnap.id, 
                    ...templateSnap.data(),
                    questions: Array.isArray(templateSnap.data()?.questions) 
                      ? templateSnap.data().questions 
                      : []
                  };
                  console.log('Fetched template from Firestore:', template);
                }
              } catch (error) {
                console.error('Error fetching template from Firestore:', error);
              }
            }
            
            if (isMounted && template) {
              setSurveyTemplate(template);
            }
          } else if (isMounted && template) {
            setSurveyTemplate(template);
          }
        }
        
        // If not found in context, try to fetch it directly
        if (!response && currentUser) {
          console.log('Response not found in context, trying to fetch directly...');
          try {
            // Try to fetch the specific assignment
            const docRef = doc(db, 'surveyAssignments', responseId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              response = { 
                id: docSnap.id, 
                ...docSnap.data(),
                // Ensure responses is always an object
                responses: docSnap.data().responses || {},
                // Check for answers in different possible locations
                ...(docSnap.data().answers && { 
                  answers: docSnap.data().answers 
                })
              };
              
              console.log('Fetched response from Firestore:', response);
              
              // If we found the response, try to get the template
              if (response.surveyId) {
                const templateRef = doc(db, 'surveyTemplates', response.surveyId);
                const templateSnap = await getDoc(templateRef);
                
                if (templateSnap.exists()) {
                  template = { 
                    id: templateSnap.id, 
                    ...templateSnap.data(),
                    // Ensure questions is always an array
                    questions: Array.isArray(templateSnap.data()?.questions) 
                      ? templateSnap.data().questions 
                      : []
                  };
                  console.log('Fetched template from Firestore:', template);
                } else {
                  console.warn('Template not found in Firestore for surveyId:', response.surveyId);
                }
              }
              
              // If we have answers but no responses, try to map them
              if (response.answers && (!response.responses || Object.keys(response.responses).length === 0)) {
                console.log('Mapping answers to responses format...');
                response.responses = { ...response.answers };
              }
            }
          } catch (error) {
            console.error('Error fetching response from Firestore:', error);
          }
        }
        
        if (!response) {
          console.error('No response found with ID:', responseId);
          // Try to find any response with a matching ID in the assignments
          const matchingAssignment = surveyAssignments.find(a => a.id === responseId);
          if (matchingAssignment) {
            console.log('Found matching assignment in context:', matchingAssignment);
            response = matchingAssignment;
          } else {
            console.error('No matching assignment found in context or Firestore');
            setLoading(false);
            return;
          }
        }

              // Check if we have all required data
        const hasAllData = response && template;
        
        if (isMounted) {
          console.log('📊 Data load status:', {
            hasResponse: !!response,
            hasTemplate: !!template,
            responseId: response?.id,
            templateId: template?.id || response?.surveyId
          });
          
          // Always update the states with the latest data
          if (response) {
            console.log('📝 Setting survey response:', response.id);
            setSurveyResponse(response);
          }
          
          if (template) {
            console.log('📋 Setting survey template:', template.id);
            setSurveyTemplate(template);
          }
          
          if (hasAllData) {
            console.log('✅ All data loaded successfully');
            setLoading(false);
          } else {
            console.log('⚠️ Missing data, will retry...', {
              hasResponse: !!response,
              hasTemplate: !!template,
              responseId: response?.id,
              templateId: template?.id || response?.surveyId
            });
            
            // If we don't have both, set a timeout to try again
            setTimeout(() => {
              if (isMounted && !hasAllData) {
                console.log('🔄 Retrying data load...');
                loadData();
              }
            }, 100);
          }
        }
        
      } catch (error) {
        console.error('Error loading survey response:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [responseId, surveyAssignments, surveyTemplates, patients, currentUser]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const matchedResponses = getMatchedResponses();
  const unmatchedResponses = getUnmatchedResponses();

  // Render loading state
  if (loading) {
    return (
      <div className="survey-response-detail">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading survey response...</p>
        </div>
      </div>
    );
  }
  
  // Check if the survey has responses or is completed
  const responseKeys = Object.keys(surveyResponse?.responses || {});
  const answerKeys = Object.keys(surveyResponse?.answers || {});
  const hasResponseData = (surveyResponse?.responses && responseKeys.length > 0) ||
                        (surveyResponse?.answers && answerKeys.length > 0);
  const isSurveyCompleted = surveyResponse?.status === 'completed' || surveyResponse?.completedDate;
  const shouldShowResponses = isSurveyCompleted || hasResponseData;
  
  // Debug log the survey response data
  console.log('Survey Response Data:', {
    status: surveyResponse?.status,
    hasResponseData,
    isSurveyCompleted,
    shouldShowResponses,
    responseKeys,
    answerKeys,
    responses: surveyResponse?.responses,
    answers: surveyResponse?.answers,
    completedDate: surveyResponse?.completedDate,
    submissionDate: surveyResponse?.submissionDate,
    lastModified: surveyResponse?.lastModified
  });
  if (loading) {
    return (
      <div className="survey-response-detail">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading survey data...</span>
          </div>
          <p>Loading survey response data...</p>
        </div>
      </div>
    );
  }
  
  // Show error message if there was an error
  if (error) {
    return (
      <div className="survey-response-detail">
        <div className="alert alert-danger">
          <h4>Error Loading Survey Response</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  // Show message if no survey response data is available
  if (!surveyResponse || !surveyTemplate) {
    return (
      <div className="survey-response-detail">
        <div className="alert alert-warning">
          <h4>No Survey Data Found</h4>
          <p>Unable to load the requested survey response.</p>
        </div>
      </div>
    );
  }
  
  // Show not completed message if the survey is assigned but has no responses
  if (!shouldShowResponses && surveyResponse?.status === 'assigned') {
    return (
      <div className="survey-response-detail">
        <div className="survey-response-header">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <button 
                onClick={() => navigate(-1)} 
                className="btn btn-link back-button"
              >
                &larr; Back to List
              </button>
              <h2 className="d-inline ms-3">Survey Response: {surveyTemplate?.title || 'Loading...'}</h2>
            </div>
            {currentUser?.role === 'clinician' && !surveyResponse?.isReviewed && (
              <button 
                onClick={handleSignOff}
                className="btn btn-primary"
                disabled={isSigningOff}
              >
                {isSigningOff ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Signing Off...
                  </>
                ) : (
                  'Sign Off as Reviewed'
                )}
              </button>
            )}
            {surveyResponse?.isReviewed && (
              <span className="badge bg-success">
                Reviewed on {new Date(surveyResponse.reviewedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        
        <div className="alert alert-info mt-4">
          <h4 className="alert-heading">Survey Not Yet Completed</h4>
          <p>This survey has been assigned to the patient but has not been completed yet.</p>
          <hr />
          <p className="mb-0">
            <strong>Status:</strong> <span className="badge bg-warning">Assigned</span><br />
            <strong>Assigned To:</strong> {patient?.name || 'Unknown Patient'}<br />
            <strong>Assigned On:</strong> {formatDate(surveyResponse.assignedDate)}
          </p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="survey-response-error">
        <h2>Error Loading Survey</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="btn btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  if (!surveyResponse) {
    return (
      <div className="survey-response-error">
        <h2>Survey Response Not Found</h2>
        <p>The requested survey response could not be found.</p>
        <button onClick={() => navigate(-1)} className="btn btn-primary">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="survey-response-detail">
      <div className="survey-response-header">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <button 
              onClick={() => navigate(-1)} 
              className="btn btn-link back-button"
            >
              &larr; Back to Patient
            </button>
            <h2 className="d-inline ms-3">{surveyTemplate?.name || 'Survey Response'}</h2>
          </div>
          {currentUser?.role === 'clinician' && !surveyResponse?.isReviewed && (
            <button 
              onClick={handleSignOff}
              className="btn btn-primary"
              disabled={isSigningOff}
            >
              {isSigningOff ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Signing Off...
                </>
              ) : (
                'Sign Off as Reviewed'
              )}
            </button>
          )}
          {surveyResponse?.isReviewed && (
            <span className="badge bg-success">
              Reviewed on {new Date(surveyResponse.reviewedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        
        <div className="survey-meta mb-3">
          {patient && (
            <div className="patient-info mb-2 d-flex align-items-center">
              <strong>Patient:</strong> {patient.name}
              <button 
                onClick={() => navigate(`/clinician/patients/${patient.id}`)}
                className="btn btn-sm btn-outline-primary ms-2"
                title="View Patient Profile"
              >
                <i className="bi bi-person-lines-fill me-1"></i> View Profile
              </button>
            </div>
          )}
          <div>
            <span className="badge bg-primary me-2">
              {surveyResponse.status?.charAt(0).toUpperCase() + (surveyResponse.status?.slice(1) || '')}
            </span>
            <span className="me-3"><strong>Assigned:</strong> {formatDate(surveyResponse.assignedDate)}</span>
            {surveyResponse.completedDate && (
              <span><strong>Completed:</strong> {formatDate(surveyResponse.completedDate)}</span>
            )}
          </div>
        </div>
      </div>

      <div className="survey-response-content">
        {matchedResponses.length > 0 ? (
          <div className="survey-questions">
            {matchedResponses.map(({ question, response }, index) => (
              <div key={question.id || index} className="question-card">
                <div className="question-header">
                  <h4>{getQuestionText(question)}</h4>
                  {question.required && (
                    <span className="question-required">Required</span>
                  )}
                  <span className="question-type">
                    {question.type ? question.type.charAt(0).toUpperCase() + question.type.slice(1) : 'Text'}
                  </span>
                </div>
                
                {question.description && (
                  <p className="question-description">{question.description}</p>
                )}
                
                <div className="question-meta">
                  <div className="meta-item">
                    <span className="meta-label">Question ID:</span>
                    <span className="meta-value">{question.id || 'N/A'}</span>
                  </div>
                  {question.category && (
                    <div className="meta-item">
                      <span className="meta-label">Category:</span>
                      <span className="meta-value">{question.category}</span>
                    </div>
                  )}
                  <div className="meta-item">
                    <span className="meta-label">Status:</span>
                    <span className={`status-badge ${response ? 'status-answered' : 'status-skipped'}`}>
                      {response ? 'Answered' : 'Skipped'}
                    </span>
                  </div>
                </div>
                
                <div className="response-answer">
                  <div className="response-header">
                    <div className="question-text">
                      <strong>Question:</strong> {getQuestionText(question)}
                    </div>
                    <span className="response-label">Response:</span>
                    {response && (
                      <span className="response-time">
                        {surveyResponse.completedDate && `Answered on ${new Date(surveyResponse.completedDate).toLocaleString()}`}
                      </span>
                    )}
                  </div>
                  <div className="response-content">
                    {response ? (
                      <div className="response-value">
                        {renderResponseValue(response, question)}
                      </div>
                    ) : (
                      <p className="no-response">No response provided</p>
                    )}
                  </div>
                </div>
                
                {question.validationError && (
                  <div className="validation-error">
                    <span className="error-icon">⚠</span>
                    <span>{question.validationError}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-responses">
            <p>No responses recorded for this survey.</p>
          </div>
        )}

        {unmatchedResponses.length > 0 && (
          <div className="unmatched-responses">
            <h4>Additional Responses</h4>
            {unmatchedResponses.map(({ key, value, questionText, isObjectResponse }) => {
              // For object responses, render each key-value pair separately
              if (isObjectResponse && value && typeof value === 'object' && !Array.isArray(value)) {
                return (
                  <div key={key} className="question-card">
                    <h4>{questionText}</h4>
                    <div className="response-answer">
                      <ul>
                        {Object.entries(value).map(([option, isSelected]) => (
                          <li key={option}>
                            <span className={`checkbox-option ${isSelected ? 'selected' : ''}`}>
                              {isSelected ? '☑' : '☐'} {option}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              }
              
              // For other types of responses
              return (
                <div key={key} className="question-card">
                  <h4>{questionText}</h4>
                  <div className="response-answer">
                    {renderResponseValue(value, {})}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {surveyResponse.notes && (
          <div className="clinician-notes">
            <h4>Clinician Notes</h4>
            <div className="notes-content">
              {surveyResponse.notes}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyResponseDetail;
